import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { HtmlEntityType } from '../../enums/HtmlEntityType';
import { Nullable } from '../../types/Nullable';
import { Button } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';

interface Props {
  /** Limit the number of visible items */
  limit: number;
  /** Source text */
  text: string;
  /** Indicates if Show More / Show Less should display as text or an icon button */
  mode?: 'text' | 'icon';
  /** Override the label for "Show less" action */
  showLessText?: string;
  /** Override the label for "Show more" action */
  showMoreText?: string;
  /** Controls the number of items to reveal (reveals all items by default) */
  step?: number;
}

/**
 *
 * @param params receives a `limit` and a `text` and will show it when `showMore` is triggered.
 * The amount of initial text shown is decided by `limit` and the amount of text shown after trigger
 * `showMore` is decided by `step`. If step is not assigned, it will use the `text.length`
 * @returns [`visibleText`, `showMore/showLess`]
 */
export const useShowMoreText = ({
  text,
  limit: initialLimit,
  mode = 'text',
  step = text.length,
  showLessText,
  showMoreText,
}: Props): {
  showMore: Nullable<JSX.Element>;
  shouldShowMore: boolean;
  visibleText: string;
  handleShowMore: () => void;
  handleShowLess: () => void;
} => {
  const intl = useIntl();
  const [limit, setLimit] = useState(initialLimit);

  useEffect(() => {
    setLimit(initialLimit);
  }, [initialLimit]);

  const hasHiddenText = text.length > initialLimit;
  const hiddenText = text.slice(limit);
  const shouldShowMore = hiddenText.length > 0;
  const visibleText =
    text.slice(0, limit) +
    (shouldShowMore ? ` [${HtmlEntityType.Ellipsis}]` : '');
  const handleShowMore = useCallback(
    () => setLimit((prevLimit) => prevLimit + step),
    [step],
  );
  const handleShowLess = useCallback(
    () => setLimit(initialLimit),
    [initialLimit],
  );
  const showMoreButtonText = showMoreText
    ? showMoreText
    : intl.formatMessage({
        id: 'UseShowMoreText.showMore',
        defaultMessage: 'Show more',
        description: 'Label for showing more text',
      });

  const showLessButtonText = showLessText
    ? showLessText
    : intl.formatMessage({
        id: 'UseShowMoreText.showLess',
        defaultMessage: 'Show less',
        description: 'Label for showing less text',
      });

  const showMore = useMemo(() => {
    if (hasHiddenText) {
      return mode === 'icon' ? (
        <IconButton
          preset={shouldShowMore ? 'show' : 'hide'}
          onClick={shouldShowMore ? handleShowMore : handleShowLess}
        />
      ) : (
        <Button
          isInline
          text={shouldShowMore ? showMoreButtonText : showLessButtonText}
          variant="action"
          onClick={shouldShowMore ? handleShowMore : handleShowLess}
        />
      );
    }
    return null;
  }, [
    hasHiddenText,
    shouldShowMore,
    mode,
    handleShowMore,
    handleShowLess,
    showMoreButtonText,
    showLessButtonText,
  ]);

  return {
    showMore,
    shouldShowMore,
    visibleText,
    handleShowMore,
    handleShowLess,
  };
};
